// Test-mode rotation helper.
//
// PM 2026-06-10: the original test-mode flag in
// `packages/frontend/src/lib/test-mode.ts` rotated puzzles every 6 minutes
// on the frontend ONLY, while the Worker still served today's UTC puzzle
// from D1. That is why "test mode" was secretly not rotating in production:
// every /start hit the Worker, the Worker computed `utcDateString()`, and
// returned the same puzzle until the actual UTC midnight tick.
//
// Fix:
//   - Bowz now sets `?test=1` (10-minute cadence, updated this turn).
//   - Frontend forwards the flag to the Worker via the
//     `X-Gives-Test-Mode` header AND the `?test=1` query param
//     (belt-and-braces - CF middleware might strip one).
//   - The Worker calls `effectiveUtcDate(realUtcDate, now)` to compute a
//     bucket string that advances every 10 minutes, and uses THAT string
//     wherever it previously used the real UTC date string for puzzle
//     selection.
//
// IMPORTANT: encrypted blobs are still derived from the REAL UTC date.
// Only puzzle SELECTION (and the JWT `dt` claim, so a fresh session is
// minted every bucket) uses the effective date.

/** How long one test-mode bucket lasts. Updated 2026-06-10: 10 minutes. */
export const TEST_MODE_BUCKET_MIN = 10;
export const TEST_MODE_BUCKET_MS = TEST_MODE_BUCKET_MIN * 60 * 1000;

/**
 * Floor-divide the current UTC time into 10-minute buckets and return a
 * date-string-shaped tag suitable for use as the "effective UTC date":
 *
 *   `<YYYY-MM-DD>-T<HH>-<MM_floor_10>`
 *
 * Example: at 2026-06-10T07:14:23Z this returns "2026-06-10-T07-10".
 *
 * The tag is sortable, opaque to the rest of the worker (treated as just
 * another string), and changes every 10 minutes so that
 * `dailySeed(gameId, bucket)` rotates puzzles on the same cadence.
 *
 * @param realUtcDate - The canonical UTC date string from `utcDateString()`.
 *                      Used as the YYYY-MM-DD prefix; the time portion comes
 *                      from `now` so callers may pass any Date for testing.
 * @param now         - The reference time. Defaults to `new Date()`.
 */
export function testModeBucket(
  realUtcDate: string,
  now: Date = new Date(),
): string {
  const hh = String(now.getUTCHours()).padStart(2, '0');
  const minFloor = Math.floor(now.getUTCMinutes() / TEST_MODE_BUCKET_MIN) * TEST_MODE_BUCKET_MIN;
  const mm = String(minFloor).padStart(2, '0');
  // Pull the date prefix from `now` rather than `realUtcDate` so the bucket
  // tag stays consistent even if a stale realUtcDate is passed.
  const yyyy = String(now.getUTCFullYear()).padStart(4, '0');
  const mo = String(now.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(now.getUTCDate()).padStart(2, '0');
  void realUtcDate;
  return `${yyyy}-${mo}-${dd}-T${hh}-${mm}`;
}

/**
 * Return the puzzle-selection date to use given test-mode state.
 *
 *   testMode = false -> the real UTC date string (production).
 *   testMode = true  -> the 10-minute bucket tag.
 *
 * Callers MUST continue to use the real UTC date for any cryptographic
 * derivation (HKDF info, AES-GCM AAD): the bucket only governs WHICH
 * puzzle index to pick. See module docstring.
 */
export function effectiveUtcDate(
  realUtcDate: string,
  opts: { testMode: boolean; now?: Date },
): string {
  if (!opts.testMode) return realUtcDate;
  return testModeBucket(realUtcDate, opts.now ?? new Date());
}

/**
 * Compute the wall-clock time (epoch ms) when the next 10-minute bucket
 * begins. The TestModeBanner uses this to drive its countdown.
 */
export function nextBucketAt(now: Date = new Date()): number {
  const ms = now.getTime();
  const remainder = ms % TEST_MODE_BUCKET_MS;
  return ms + (TEST_MODE_BUCKET_MS - remainder);
}

/**
 * Read the test-mode signal from an HTTP request. Accepts EITHER the
 * `X-Gives-Test-Mode: 1` header (preferred - survives CDN query stripping)
 * OR the `?test=1` query string (preferred - survives header stripping by
 * over-eager middleware). Either one being truthy enables test mode.
 */
export function isTestModeRequest(req: Request): boolean {
  try {
    const headerVal = req.headers.get('x-gives-test-mode');
    if (headerVal && headerVal.trim() === '1') return true;
  } catch {
    /* ignore */
  }
  try {
    const u = new URL(req.url);
    if (u.searchParams.get('test') === '1') return true;
  } catch {
    /* ignore */
  }
  return false;
}
